/**
 * Centralized permission helpers for the multi-role user system (frontend).
 *
 * Mirrors contracting-api/src/utils/permissions.js but contains only synchronous,
 * data-driven predicates (no DB / network access). Components must never compare
 * user.role === 'x' directly - go through these helpers so the "most capable
 * role wins" rule is applied consistently.
 *
 * A user object on the frontend looks like: { id, ..., roles: string[] }.
 */

export const ROLES = Object.freeze({
  // Platform operator: company-less account that selects a company and then
  // operates with owner-level access to it (see userSlice / API isAuth).
  SUPER_ADMIN: 'super_admin',
  OWNER: 'owner',
  GENERAL_MANAGER: 'general_manager',
  ACCOUNTANT: 'accountant',
  ADMIN: 'admin',
  DISPATCHER: 'dispatcher',
  TECHNICIAN: 'technician',
});

// Highest-capability role first. primaryRole(user) returns the most capable role they hold.
export const ROLE_PRECEDENCE = Object.freeze([
  ROLES.OWNER,
  ROLES.GENERAL_MANAGER,
  ROLES.ACCOUNTANT,
  ROLES.ADMIN,
  ROLES.DISPATCHER,
  ROLES.TECHNICIAN,
]);

// Roles assignable through the Add/Edit user UI. Owner is intentionally excluded.
export const ASSIGNABLE_ROLES = Object.freeze([
  ROLES.GENERAL_MANAGER,
  ROLES.ACCOUNTANT,
  ROLES.ADMIN,
  ROLES.DISPATCHER,
]);

export const ROLE_LABELS = Object.freeze({
  [ROLES.OWNER]: 'Owner',
  [ROLES.GENERAL_MANAGER]: 'General Manager',
  [ROLES.ACCOUNTANT]: 'Accountant',
  [ROLES.ADMIN]: 'Admin',
  [ROLES.DISPATCHER]: 'Dispatcher',
  [ROLES.TECHNICIAN]: 'Technician',
});

export const formatRoleLabel = (role) => ROLE_LABELS[role] || (role ? String(role) : '');

// ---------------------------------------------------------------------------
// Pure-role primitives.
// ---------------------------------------------------------------------------

export const hasRole = (user, role) =>
  Array.isArray(user?.roles) && user.roles.includes(role);

export const hasAnyRole = (user, roles) =>
  Array.isArray(user?.roles) &&
  Array.isArray(roles) &&
  roles.some((r) => user.roles.includes(r));

/** Most-capable role the user holds, or null. Useful for UI display. */
export const primaryRole = (user) =>
  ROLE_PRECEDENCE.find((r) => hasRole(user, r)) || null;

export const primaryRoleLabel = (user) => formatRoleLabel(primaryRole(user));

/** All roles the user holds, highest-capability first. */
export const userRoles = (user) =>
  ROLE_PRECEDENCE.filter((r) => hasRole(user, r));

/** Comma-separated display labels for every role the user holds. */
export const allRoleLabels = (user) =>
  userRoles(user).map(formatRoleLabel).join(', ');

// ---------------------------------------------------------------------------
// Named role groups.
// ---------------------------------------------------------------------------

/** Platform super-admin (company-less operator). */
export const isSuperAdmin = (u) => hasRole(u, ROLES.SUPER_ADMIN);

export const isOwner = (u) => hasRole(u, ROLES.OWNER);
export const isGeneralManager = (u) => hasRole(u, ROLES.GENERAL_MANAGER);
export const isAccountant = (u) => hasRole(u, ROLES.ACCOUNTANT);
export const isAdmin = (u) => hasRole(u, ROLES.ADMIN);
export const isDispatcher = (u) => hasRole(u, ROLES.DISPATCHER);
export const isTechnician = (u) => hasRole(u, ROLES.TECHNICIAN);

/** Owner / accountant / general_manager - acts on financial fields. */
export const isAccounting = (u) =>
  hasAnyRole(u, [ROLES.OWNER, ROLES.ACCOUNTANT, ROLES.GENERAL_MANAGER]);

/** Owner / general_manager - top-level decision makers. */
export const isManagement = (u) =>
  hasAnyRole(u, [ROLES.OWNER, ROLES.GENERAL_MANAGER]);

/**
 * "Owner-like" gating used in many places: owner OR general_manager OR admin.
 * Mirrors the recurring `user.role === 'owner' || ... || 'admin'` pattern.
 */
export const isOwnerOrAdminOrManagement = (u) =>
  hasAnyRole(u, [ROLES.OWNER, ROLES.GENERAL_MANAGER, ROLES.ADMIN]);

// ---------------------------------------------------------------------------
// Relationship helpers.
// ---------------------------------------------------------------------------

/** True if the user (admin) appears in the client's admins[] list. */
export const adminHasClientAccess = (user, client) => {
  if (!user || !client) return false;
  const admins = client.admins || [];
  return admins.some((a) => (typeof a === 'object' ? a?.id : a) === user.id);
};

/** True for dispatchers assigned to the given job. */
export const isAssignedDispatcher = (user, job) =>
  isDispatcher(user) && !!job && job.dispatcher_id === user.id;

// ---------------------------------------------------------------------------
// Page / navigation gating.
// ---------------------------------------------------------------------------

export const canAccessUsersPage = (u) => isManagement(u);
export const canViewDeletedJobs = (u) => isManagement(u);
/** Accounting screens (view data). Admins see only their clients' jobs via API scoping. */
export const canAccessAccounting = (u) => isAccounting(u) || isAdmin(u);
export const canViewDashboard = (u) =>
  hasAnyRole(u, [
    ROLES.OWNER,
    ROLES.GENERAL_MANAGER,
    ROLES.ACCOUNTANT,
    ROLES.ADMIN,
  ]);
/**
 * Control Center: owner / general_manager / accountant see company-wide data.
 * Admins may enter when admin is their most-capable role; API scopes to
 * user_client_access. Higher roles always win over admin (same as Dashboard).
 */
export const canViewControlCenter = (u) =>
  hasAnyRole(u, [
    ROLES.OWNER,
    ROLES.GENERAL_MANAGER,
    ROLES.ACCOUNTANT,
    ROLES.ADMIN,
  ]);
/** Owner Alerts tab: scheduled reports, groups, refined announce. */
export const canManageAlerts = (u) => isOwner(u);
export const canAccessClientsPage = (u) => !isOnlyDispatcher(u);
export const canAccessRequestsPage = (u) =>
  hasAnyRole(u, [
    ROLES.OWNER,
    ROLES.GENERAL_MANAGER,
    ROLES.ACCOUNTANT,
    ROLES.ADMIN,
  ]);
export const canAccessProposalsTab = (u) =>
  hasAnyRole(u, [ROLES.OWNER, ROLES.GENERAL_MANAGER, ROLES.ADMIN]);
export const canAccessPendingJobsTab = (u) =>
  hasAnyRole(u, [ROLES.OWNER, ROLES.GENERAL_MANAGER, ROLES.ADMIN]);
export const canAccessPartRequestsTab = (u) =>
  hasAnyRole(u, [
    ROLES.OWNER,
    ROLES.GENERAL_MANAGER,
    ROLES.ADMIN,
    ROLES.ACCOUNTANT,
  ]);
/** Job-level unlock requests on the Requests page (owner / GM / accountant). */
export const canAccessJobUnlockRequestsTab = (u) => canUnlockJob(u);

/** True when the user holds dispatcher only (no other capable role). Drives several UI restrictions. */
export const isOnlyDispatcher = (u) =>
  isDispatcher(u) &&
  !isOwner(u) &&
  !isAdmin(u) &&
  !isManagement(u) &&
  !isAccountant(u);

// ---------------------------------------------------------------------------
// Clients / POCs / technicians.
// ---------------------------------------------------------------------------

export const canManageClientsDirectly = (u) => isOwner(u);
export const canRequestClient = (u) =>
  hasAnyRole(u, [ROLES.GENERAL_MANAGER, ROLES.ACCOUNTANT, ROLES.ADMIN, ROLES.DISPATCHER]);
export const canAcceptClientRequests = (u) => isManagement(u);
export const canAddOrEditClient = (u) =>
  hasAnyRole(u, [ROLES.OWNER, ROLES.ADMIN]);
export const canManageClientPocs = (u) =>
  hasAnyRole(u, [ROLES.OWNER, ROLES.GENERAL_MANAGER, ROLES.ADMIN]);
export const canViewClientPocs = canManageClientPocs;
export const canManageTechnicians = (u) =>
  hasAnyRole(u, [ROLES.OWNER, ROLES.GENERAL_MANAGER, ROLES.ADMIN]);
export const canCreateTechnician = (u) =>
  hasAnyRole(u, [ROLES.OWNER, ROLES.GENERAL_MANAGER, ROLES.ADMIN, ROLES.DISPATCHER]);
export const canBlacklistTechnician = canManageTechnicians;

// ---------------------------------------------------------------------------
// Jobs - financials.
// ---------------------------------------------------------------------------

export const canMarkJobPaid = (u) => isAccounting(u);
export const canMarkJobInvoiced = (u) => isAccounting(u);
export const canMarkJobFactored = (u) => isAccounting(u);
export const canMarkJobQuickbooksInvoiced = (u) => isAccounting(u);
/** Role eligibility for sent_to_client (lock state gated by canEditSentToClientOnJob). */
export const canMarkJobSentToClient = (u) => isAccounting(u) || isAdmin(u);

/**
 * Whether the user may toggle sent_to_client for a job in this lock state.
 * Accounting (owner / GM / accountant): always. Admin-only: when locked.
 * API also checks user_client_access for admins.
 */
export const canEditSentToClientOnJob = (u, isJobLocked) => {
  if (!canMarkJobSentToClient(u)) return false;
  if (isAccounting(u)) return true;
  return !!isJobLocked;
};

/** Full QB invoice access (upload / manage / see private). Owner / GM / accountant. */
export const canSeeQuickbooksInvoice = (u) => isAccounting(u);
/**
 * List / preview / download Quickbooks invoice attachments.
 * Accounting: all QB files. Admin: public QB only (API also requires client access).
 */
export const canViewQuickbooksInvoiceAttachments = (u) =>
  isAccounting(u) || isAdmin(u);

/**
 * Filter job attachments for display: accounting sees all QB; admin sees public QB
 * only; others never see QB.
 */
export const filterJobAttachmentsForUser = (attachments, user) => {
  const list = attachments || [];
  if (canSeeQuickbooksInvoice(user)) return list;
  if (isAdmin(user)) {
    return list.filter(
      (att) => att.category !== 'quickbooks_invoice' || !att.is_private
    );
  }
  return list.filter((att) => att.category !== 'quickbooks_invoice');
};

// ---------------------------------------------------------------------------
// Jobs - editing & creation.
// ---------------------------------------------------------------------------

/** Owner / GM / admin can create jobs and toggle statuses. */
export const canCreateOrEditJob = (u) =>
  hasAnyRole(u, [ROLES.OWNER, ROLES.GENERAL_MANAGER, ROLES.ADMIN]);

/**
 * Edit a single job. Owner/GM/admin always; dispatchers only on their assigned jobs.
 */
export const canEditJob = (user, job) => {
  if (canCreateOrEditJob(user)) return true;
  return isAssignedDispatcher(user, job);
};

/** Edit job notes: owner / accountant / GM, or admin (caller must check client access). */
export const canEditJobNotes = (u) => isAccounting(u) || isAdmin(u);

// ---------------------------------------------------------------------------
// Job locking (mirrors contracting-api jobLocksDao behaviour).
// While a job is locked, most write paths return HTTP 423. Owner / accountant /
// general_manager may still patch invoice bookkeeping (QB invoiced, factored,
// paid, proposal invoice_number), edit the invoiced proposal in place, and
// edit existing technician assignments (cost, dates, status, notes).
// ---------------------------------------------------------------------------

/** Owner / GM / accountant can directly unlock a job (resets status to Open). */
export const canUnlockJob = (u) => isManagement(u) || isAccountant(u);

/** Any user who can't unlock directly, but has job access, can ask owner / GM / accountant to unlock. */
export const canRequestJobUnlock = (user, job) => {
  if (!job) return false;
  if (canUnlockJob(user)) return false;
  return canAccessJobChild(user, job);
};

// ---------------------------------------------------------------------------
// Job comments / children (parts, proposals, technicians).
// ---------------------------------------------------------------------------

/** Most domain children share this rule: owner/GM/accountant/admin always; dispatcher only when assigned. */
export const canAccessJobChild = (user, job) => {
  if (isAccounting(user) || isAdmin(user)) return true;
  return isAssignedDispatcher(user, job);
};
export const canCreateJobChild = canAccessJobChild;

// ---------------------------------------------------------------------------
// Proposals.
// ---------------------------------------------------------------------------

export const canCreateProposal = canAccessJobChild;
export const canLockUnlockProposal = (u) =>
  hasAnyRole(u, [ROLES.OWNER, ROLES.GENERAL_MANAGER, ROLES.ADMIN]);
export const canToggleProposalApproval = canLockUnlockProposal;
export const canRejectProposal = canLockUnlockProposal;
// Mirrors API permissions.canInvoiceProposal / proposalsDao — owner / accountant / GM only.
export const canInvoiceProposal = (u) => isAccounting(u);
export const canUninvoiceProposal = (u) => isOwner(u);
export const canEditInvoiceNumber = (u) =>
  hasAnyRole(u, [ROLES.OWNER, ROLES.ACCOUNTANT, ROLES.GENERAL_MANAGER]);

export const canEditProposal = (user, proposal, job) => {
  if (isAccounting(user)) return true;
  if (isAdmin(user)) return true;
  if (
    isAssignedDispatcher(user, job) &&
    proposal?.created_by === user.email &&
    !proposal?.is_locked &&
    !proposal?.invoiced
  ) {
    return true;
  }
  return false;
};

export const canDeleteProposal = (user, proposal) => {
  if (isOwner(user)) return true;
  return proposal?.created_by === user.email;
};

export const canRequestProposalApproval = (user, proposal, job) =>
  isAssignedDispatcher(user, job) && proposal?.created_by === user.email;

export const canRequestProposalUnlock = (user, proposal, job) => {
  if (!proposal?.is_locked && !proposal?.invoiced) return false;
  // Locker never requests their own unlock (use Unlock instead when permitted)
  const lockedBy = (proposal?.locked_by || '').toLowerCase();
  const userEmail = (user?.email || '').toLowerCase();
  if (lockedBy && userEmail && lockedBy === userEmail) return false;
  if (isAdmin(user)) return true;
  return isAssignedDispatcher(user, job);
};

// ---------------------------------------------------------------------------
// Parts.
// ---------------------------------------------------------------------------

export const canViewPart = (user, job) => {
  if (hasAnyRole(user, [ROLES.OWNER, ROLES.ACCOUNTANT])) return true;
  if (isAdmin(user)) return true;
  return isAssignedDispatcher(user, job);
};
export const canCreatePart = canViewPart;
export const canLockUnlockPart = (u) =>
  hasAnyRole(u, [ROLES.OWNER, ROLES.ACCOUNTANT]);
export const canTogglePartApproval = canLockUnlockPart;
export const canRejectPart = canLockUnlockPart;

export const canEditPart = (user, part, job) => {
  if (hasAnyRole(user, [ROLES.OWNER, ROLES.ACCOUNTANT])) return true;
  if (isAdmin(user)) return !part?.is_locked;
  if (
    isAssignedDispatcher(user, job) &&
    part?.created_by === user.email &&
    !part?.is_locked
  ) {
    return true;
  }
  return false;
};

export const canDeletePart = (user, part) => {
  if (part?.is_locked) return false;
  if (hasAnyRole(user, [ROLES.OWNER, ROLES.ACCOUNTANT])) return true;
  return part?.created_by === user.email;
};

export const canRequestPartApproval = (user, _part, job) => {
  if (isAdmin(user)) return true;
  return isAssignedDispatcher(user, job);
};

export const canRequestPartUnlock = (user, part, job) => {
  if (!part?.is_locked) return false;
  if (isAdmin(user)) return true;
  return isAssignedDispatcher(user, job);
};

// ---------------------------------------------------------------------------
// Job technicians.
// ---------------------------------------------------------------------------

export const canCreateJobTechnician = canAccessJobChild;
export const canEditJobTechnician = canCreateJobTechnician;
export const canDeleteJobTechnician = canCreateJobTechnician;
/** Mirrors API: accounting / admin / assigned dispatcher. UI may still leave the field open and let the API enforce. */
export const canSetJobTechnicianPaidDate = canEditJobTechnician;
/** Accounting or admin. Dispatchers cannot lock. */
export const canLockJobTechnician = (u) => isAccounting(u) || isAdmin(u);
export const canUnlockJobTechnician = (u) => isOwner(u);
export const canEditLockedJobTechnician = (u) => isAccounting(u);
export const canDeleteLockedJobTechnician = (u) => isOwner(u);
export const canUploadJobTechnicianAttachment = (u) =>
  hasAnyRole(u, [ROLES.OWNER, ROLES.ACCOUNTANT]);
export const canDeleteJobTechnicianAttachment = (u) => isOwner(u);

export const canRequestJobTechnicianUnlock = (user, job) => {
  if (isAccountant(user)) return true;
  if (isAdmin(user)) return true;
  return isAssignedDispatcher(user, job);
};

// ---------------------------------------------------------------------------
// Attachments / privacy.
// ---------------------------------------------------------------------------

/**
 * Owner / GM see all private attachments; admin sees private when context
 * allows (caller resolves access). Accountants do NOT see other users'
 * private attachments here - the API separately always returns an
 * accountant's own uploads regardless of privacy.
 */
export const canSeePrivateAttachment = (u) => isManagement(u) || isAdmin(u);
/** Owner / GM / accountant / admin can mark a newly-uploaded attachment private. */
export const canUploadPrivateAttachment = (u) => isAccounting(u) || isAdmin(u);
/**
 * Owner / GM / admin can toggle privacy on any attachment; accountants only on
 * their own upload. Admins cannot change privacy on quickbooks_invoice (view-only).
 */
export const canUpdateAttachmentPrivacy = (user, attachment) => {
  if (attachment?.category === 'quickbooks_invoice' && !canSeeQuickbooksInvoice(user)) {
    return false;
  }
  return (
    canSeePrivateAttachment(user) ||
    (isAccountant(user) && attachment?.uploaded_by === user?.id)
  );
};

/**
 * Job attachments: owner / GM / admin / assigned dispatcher may manage (edit
 * category, drag-and-drop) any attachment. Accountants are limited to
 * attachments they uploaded themselves. Admins cannot manage quickbooks_invoice
 * (view-only). Does not account for job lock — use
 * `canManageJobAttachmentOnJob` when lock state matters.
 */
export const canManageJobAttachment = (user, attachment, job) => {
  if (attachment?.category === 'quickbooks_invoice' && !canSeeQuickbooksInvoice(user)) {
    return false;
  }
  if (isManagement(user) || isAdmin(user)) return true;
  if (isAssignedDispatcher(user, job)) return true;
  return isAccountant(user) && attachment?.uploaded_by === user?.id;
};

/** Like canManageJobAttachment but owner / GM bypass a locked job. */
export const canManageJobAttachmentOnJob = (user, attachment, job, isJobLocked = false) => {
  if (!canManageJobAttachment(user, attachment, job)) return false;
  if (isJobLocked && !isManagement(user)) return false;
  return true;
};

/** Like canUpdateAttachmentPrivacy but owner / GM bypass a locked job. */
export const canUpdateAttachmentPrivacyOnJob = (user, attachment, isJobLocked = false) => {
  if (!canUpdateAttachmentPrivacy(user, attachment)) return false;
  if (isJobLocked && !isManagement(user)) return false;
  return true;
};

/**
 * Whether the user may delete this job attachment.
 * @param {boolean} [isJobLocked=false] — when true, only owner/GM (any file) or
 *   accountant (quickbooks_invoice) may delete; mirrors uploadsController.deleteFile.
 */
export const canDeleteJobAttachment = (user, attachment, job, isJobLocked = false) => {
  const isQuickbooksInvoice = attachment?.category === 'quickbooks_invoice';

  if (isManagement(user)) return true;
  if (isAccountant(user) && isQuickbooksInvoice) return true;

  if (isJobLocked) return false;

  if (!canManageJobAttachment(user, attachment, job)) return false;
  return attachment?.uploaded_by === user?.id;
};

// ---------------------------------------------------------------------------
// Company profile.
// ---------------------------------------------------------------------------

export const canEditCompanyProfile = (u) =>
  hasAnyRole(u, [ROLES.SUPER_ADMIN, ROLES.OWNER]);
export const canViewPrivateCompanyAttachments = canEditCompanyProfile;

/** Company AI prompt templates (proposal / job description). Owner, GM, accountant, super_admin. */
export const canManageAiPromptTemplates = (u) =>
  isAccounting(u) || isSuperAdmin(u);
